import { get, ref, runTransaction } from 'firebase/database';
import { database } from '../../firebase';

const invalidKey = /[.#$\[\]/\u0000-\u001f\u007f]/;

export async function listShortlists(companyId) {
  if (!companyId || invalidKey.test(companyId)) throw new Error('Company is unavailable.');
  const snapshot = await get(ref(database, `companies/${companyId}/tal_shortlists`));
  return Object.entries(snapshot.val() || {}).map(([name, value]) => ({
    name, count: Object.keys(value.profiles || {}).length,
  })).sort((a, b) => a.name.localeCompare(b.name));
}

export function validShortlistName(name) {
  return !!name.trim() && name.trim().length <= 100 && !invalidKey.test(name);
}

export async function saveToShortlist(companyId, name, rows, searchContext) {
  if (!companyId || invalidKey.test(companyId) || !validShortlistName(name) || !rows.length) {
    throw new Error('Choose a shortlist and at least one candidate.');
  }
  const entries = {};
  for (const row of rows) {
    const meta = row.profile.metadata;
    const id = meta.pinecone_id || meta.user_uid || row.id;
    if (!id) throw new Error('A candidate is missing its profile ID.');
    const key = id.replace(/[.#$\[\]/\u0000-\u001f\u007f]/g, '_');
    const cv = Object.fromEntries(['about', 'work_experience', 'education', 'skills', 'certifications', 'languages',
      'projects', 'source_updated_at', 'fetched_at', 'details_version'].filter((field) => meta.full_cv?.[field] != null)
      .map((field) => [field, meta.full_cv[field]]));
    // Match the existing shortlist format. Save professional data only: selecting
    // a candidate must never unlock contacts or overwrite an earlier unlock.
    entries[key] = JSON.parse(JSON.stringify({
      pinecone_id: meta.pinecone_id || row.id, user_uid: meta.user_uid || key,
      fromPinecone: !!meta.pinecone_id, source: meta.source || '',
      crustdata_person_id: meta.crustdata_person_id,
      name: meta.name || meta.username || '', username: meta.name || meta.username || '',
      current_role: meta.current_role || '', current_company: meta.current_company || '',
      jobTitle: meta.current_role || '', company: meta.current_company || '',
      location: meta.location || '', picture: meta.picture || '',
      linkedin: meta.linkedin_url || '', linkedin_url: meta.linkedin_url || '',
      tags: meta.tags || [], experience: meta.experience || '', education: meta.education || '',
      query_matches: meta.query_matches || [],
      company_info: meta.company_info || '', institution_info: meta.institution_info || '',
      full_cv: Object.keys(cv).length ? cv : null,
      agentAssessment: searchContext?.runId ? {
        runId: searchContext.runId, query: searchContext.query || '', score: row.score ?? null,
        matched: (row.matched || []).map(check => ({ criterion_id: check.criterion_id, label: check.label,
          label_ar: check.label_ar, display_language: check.display_language, explanation: check.explanation || '' })),
        gaps: (row.gaps || []).map(check => ({ criterion_id: check.criterion_id, label: check.label,
          label_ar: check.label_ar, display_language: check.display_language, status: check.status || 'unknown' })),
        analysis: row.analysis || null, requiredConflict: !!row.required_conflict, savedAt: Date.now(),
      } : null,
    }));
  }
  const createdAt = Date.now();
  // One atomic transaction for the entire selection. Retrying or re-selecting
  // the same person cannot duplicate records, lose other additions, or replace
  // richer profile/contact data already saved by the employer.
  const result = await runTransaction(ref(database, `companies/${companyId}/tal_shortlists/${name.trim()}`), (current) => {
    const profiles = { ...(current?.profiles || {}) };
    for (const [key, value] of Object.entries(entries)) profiles[key] = { ...value, ...profiles[key],
      ...(value.agentAssessment && !profiles[key]?.agentAssessment ? { agentAssessment: value.agentAssessment } : {}) };
    return { ...current, createdAt: current?.createdAt || createdAt, updatedAt: createdAt,
      ...(searchContext?.query && !current?.searchContext ? { searchContext: { query: searchContext.query, runId: searchContext.runId || '' } } : {}), profiles };
  }, { applyLocally: false });
  if (!result.committed) throw new Error('The shortlist could not be saved.');
  return Object.keys(entries).length;
}
